import React, { useContext, useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import { Employee } from '@ux/header-util';
import useCookie from 'react-use/lib/useCookie';

import hasWindow from '../lib/hasWindow';
import { HEADER_TO_TYPE, TYPES } from '../lib/types';

/**
 * @typedef {T[keyof T]} ValueOf
 * @template T
 */
/**
 * @typedef {Object} ShopperUserData
 * @prop {import('@ux/header-util/lib/customer').default} shopperData Full shoppper data object
 * @prop {Object} uxData Data from server props
 * @prop {string} fullName Full name of shopper
 * @prop {string} username User name of shopper
 * @prop {Object} pcx Partners experience settings
 * @prop {Object} vip VIP settings
 * @prop {boolean} loggedIn Whether a user is logged in
 * @prop {number} items How many items are in the shopper's cart
 */
/**
 * @typedef {Object} EmployeeUserData
 * @prop {import('@ux/header-util/lib/employee').default} employeeData Full employee data object
 * @prop {Object} uxData Data from server props
 * @prop {string} fullName Full name of employee
 * @prop {string} username User name of employee
 * @prop {boolean} loggedIn Whether a user is logged in
 */
/**
 * @typedef {Object} UserDataShape
 * @prop {ShopperUserData | EmployeeUserData | {}} userData Parsed user data
 * @prop {boolean} userDataLoaded Whether user data has been loaded yet
 * @prop {T & { requestedHeader: ValueOf<TYPES> }} rootProps Props from the wrapped component, plus normalized requestedHeader
 * @template T
 */
/**
 * @type {React.Context<UserDataShape<any> | {}>}
 */
const UserDataContext = React.createContext({});

export const getUxData = () => {
  if (hasWindow()) {
    return get(window, 'ux._config.props', {});
  }
  return {};
};

export const UserDataProvider = (props) => {
  const [userData, setUserData] = useState({});
  const [userDataLoaded, setUserDataLoaded] = useState({});

  // get the idp info for username
  const defaultIdp = { firstname: '', lastname: '' };
  let idpInfo;
  try {
    const [idpInfoStr] = useCookie('info_idp');
    idpInfo = JSON.parse(idpInfoStr) || defaultIdp;
  } catch (e) {
    idpInfo = defaultIdp;
    // gasket.logger.error(`error could not get idp cookie info now using default due to: ${e}`);
  }

  const requestedHeader = useMemo(() => {
    return props.requestedHeader && HEADER_TO_TYPE[props.requestedHeader];
  }, [props.requestedHeader]);

  const getUsername = (shopperData, idpInfo) => {
    const { username: shopperUsername } = shopperData;
    if (shopperUsername && shopperUsername.length > 0) {
      return shopperUsername;
    }
    if (idpInfo.username && idpInfo.username.length > 0) {
      return idpInfo.username;
    }
    return `${idpInfo.firstname} ${idpInfo.lastname}`;
  };

  useEffect(() => {
    if (hasWindow()) {
      const uxData = getUxData();
      if (requestedHeader === TYPES.internal) {
        const employeeData = new Employee(props);
        employeeData.updateFromCookie();
        setUserDataLoaded(!!employeeData && !isEmpty(employeeData));
        const fullName = employeeData.displayName(false);
        setUserData({
          employeeData,
          uxData,
          fullName,
          username: employeeData.get('loginName'),
          loggedIn: employeeData.loggedIn
        });
      } else {
        const shopperData = props.customer || {};
        const { vip, name: fullName, pcx = false } = shopperData;

        const username = getUsername(shopperData, idpInfo);
        setUserDataLoaded(!!props.customer && !isEmpty(props.customer));

        const loggedIn = shopperData.loggedIn || (!!idpInfo.firstname || !!idpInfo.lastname);
        const items = userDataLoaded && shopperData.items;

        setUserData({
          shopperData,
          uxData,
          fullName,
          username,
          pcx,
          vip,
          loggedIn,
          items
        });
      }
    }
  }, [idpInfo.firstname, idpInfo.lastname, idpInfo.username, props, props.customer, requestedHeader, userDataLoaded]);

  /**
   * @type {UserDataShape<typeof props>}
   */
  const value = {
    userData,
    userDataLoaded,
    rootProps: {
      ...props,
      requestedHeader
    }
  };

  return (
    <UserDataContext.Provider value={ value }>
      {props.children}
    </UserDataContext.Provider>
  );
};

UserDataProvider.propTypes = {
  customer: PropTypes.shape({}),
  children: PropTypes.node.isRequired,
  requestedHeader: PropTypes.string
};

export const useUserData = () => {
  return useContext(UserDataContext);
};

export const useRootProps = () => {
  // @ts-expect-error It is ok if this returns undefined when the user data isn't ready yet
  return useContext(UserDataContext).rootProps;
};
